export interface PathfinderPttUtterance {
  id: number;
  device_id: string;
  entity_id?: string | null;
  course_id?: string | null;
  channel_id: string;
  started_at: string;
  ended_at: string;
  latitude?: number | null;
  longitude?: number | null;
  transcript?: string | null;
  team?: string | null;
  sender?: string | null;
}

function overlaps(
  u: PathfinderPttUtterance,
  start: Date,
  end: Date,
): boolean {
  const a = new Date(u.started_at).getTime();
  const b = new Date(u.ended_at).getTime();
  return a <= end.getTime() && b >= start.getTime();
}

class PttUtterancesService {
  private baseUrl = '/api/ptt';

  private get authHeaders(): Record<string, string> {
    return {
      Authorization: `Bearer ${localStorage.getItem('access_token') || ''}`,
    };
  }

  async list(params: {
    entity_id?: string;
    /** Comma-separated; used for the loaded entities plus co-travelers. */
    entity_ids?: string;
    start?: string;
    end?: string;
    q?: string;
    limit?: number;
  }): Promise<PathfinderPttUtterance[]> {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) search.set(key, String(value));
    });
    const res = await fetch(`${this.baseUrl}/utterances?${search}`, {
      headers: this.authHeaders,
    });
    if (!res.ok) return [];
    const body = await res.json();
    return body.utterances || [];
  }

  inWindow(
    utterances: PathfinderPttUtterance[],
    window: { start: Date | null; end: Date | null } | null,
    query?: string,
  ): PathfinderPttUtterance[] {
    let rows = utterances;
    if (window?.start && window?.end) {
      rows = rows.filter((u) => overlaps(u, window.start as Date, window.end as Date));
    }
    if (query && query.trim()) {
      const needle = query.trim().toLowerCase();
      rows = rows.filter((u) => (u.transcript || '').toLowerCase().includes(needle));
    }
    return rows;
  }

  /**
   * Live tail over `GET /api/ptt/stream`. Only utterances from `entityIds`
   * (entity or device id) are forwarded. Returns a disposer. Reconnects with
   * backoff while mounted; `onStatus` reports connection state for the badge.
   */
  stream(opts: {
    entityIds: Set<string>;
    onUtterance: (u: PathfinderPttUtterance) => void;
    onStatus?: (connected: boolean) => void;
  }): () => void {
    let es: EventSource | null = null;
    let closed = false;
    let attempt = 0;
    let retryTimer: number | null = null;

    const open = () => {
      if (closed) return;
      const token = localStorage.getItem('access_token') || '';
      const url = `${this.baseUrl}/stream${token ? `?access_token=${encodeURIComponent(token)}` : ''}`;
      es = new EventSource(url);
      es.addEventListener('ready', () => {
        attempt = 0;
        opts.onStatus?.(true);
      });
      es.addEventListener('utterance', (ev: MessageEvent) => {
        try {
          const u = JSON.parse(ev.data) as PathfinderPttUtterance;
          const keys = [u.entity_id, u.device_id].filter(Boolean) as string[];
          if (opts.entityIds.size && !keys.some((k) => opts.entityIds.has(k))) return;
          if (!u.started_at) return;
          opts.onUtterance({ ...u, ended_at: u.ended_at || u.started_at });
        } catch {
          /* ignore malformed event */
        }
      });
      es.onerror = () => {
        opts.onStatus?.(false);
        es?.close();
        es = null;
        if (closed) return;
        attempt += 1;
        const delay = Math.min(30_000, 1_000 * 2 ** Math.min(attempt, 5));
        retryTimer = window.setTimeout(open, delay);
      };
    };

    open();
    return () => {
      closed = true;
      if (retryTimer) window.clearTimeout(retryTimer);
      es?.close();
      es = null;
      opts.onStatus?.(false);
    };
  }

  /** Line that is live at `at`, or the most recent line already spoken. */
  activeId(utterances: PathfinderPttUtterance[], at: Date | null | undefined): number | null {
    if (!at || utterances.length === 0) return null;
    const t = at.getTime();
    let covering: PathfinderPttUtterance | null = null;
    let prior: PathfinderPttUtterance | null = null;
    for (const u of utterances) {
      const start = new Date(u.started_at).getTime();
      const end = new Date(u.ended_at).getTime();
      if (start <= t && t <= end) covering = u;
      else if (start <= t) prior = u;
    }
    return covering?.id ?? prior?.id ?? null;
  }
}

export const pttUtterancesService = new PttUtterancesService();
